/**
 * `<Tape>` — the public print stream for one market.
 *
 * A trade is **never revised**, so this is an append-only log and rows only ever
 * enter at the top. That makes React's job easy — a keyed list with a stable key
 * and no reordering — and it is why the tape, unlike the ladder, does not need
 * to opt out of reconciliation. At its 25 f/s peak it appends 25 rows a second
 * against a 500-row bound, which the reconciler handles inside a frame.
 *
 * **That is a fact about the public tape, not about the venue, and §F31 is where
 * the two came apart.** There *is* a matched-then-mined progression — a match is
 * agreed at the sequencer and its `matchOrders` transaction lands on Arbitrum
 * some time later (D-B1) — and `<FillsTable>` renders both tiers, because a
 * `fills` row is revised in place when its transaction is confirmed. The public
 * tape carries only the first tier: a trade is a public fact about a price, and
 * the transaction that settled it is nobody else's business. `TradeData` has
 * neither `version` nor `settlement_tx_hash`, and neither is coming.
 *
 * `taker_outcome` and `taker_side` say how the aggressor expressed the trade, so
 * a `SELL UP` at 63¢ and a `BUY DOWN` at 37¢ are both visible as what the trader
 * actually did — the two are the same interest on one ladder (D1) and collapsing
 * them would hide the thing the tape exists to show.
 *
 * **No account identifiers appear on the public tape, ever.**
 */

import { Glyph } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatShares } from '@/lib/format'

export interface TapeTrade {
  trade_id?: string
  price_cents?: number
  size?: string
  taker_side?: 'BUY' | 'SELL'
  taker_outcome?: 'UP' | 'DOWN'
  executed_at_ms?: number
  seq?: number
}

export interface TapeProps {
  trades: readonly TapeTrade[]
  /** Rows to render. The store keeps 500; the panel shows what fits. */
  limit?: number
  className?: string
}

export function Tape({ trades, limit = 20, className }: TapeProps) {
  const rows = trades.slice(0, limit)

  if (rows.length === 0) {
    // `no trades yet` — never `No data`, which is banned on this surface
    // (docs/07 §5). A new market has no prints because it just opened, which is
    // a fact about the clock.
    return (
      <div className={cx('grid place-items-center py-4', className)}>
        <span className="text-body-sm text-text-tertiary">no trades yet</span>
      </div>
    )
  }

  return (
    <div className={cx('flex flex-col', className)}>
      <div className="flex items-center px-2 py-1 text-micro text-text-tertiary">
        <span className="w-12">Price</span>
        <span className="flex-1 text-right">Size</span>
        <span className="w-16 text-right">Time</span>
      </div>
      {rows.map((trade, i) => {
        // The *aggressor's* direction, which is what a tape reports. A `BUY UP`
        // and a `SELL DOWN` both lift the UP offer and both render as up.
        const up = trade.taker_outcome === 'UP' ? trade.taker_side === 'BUY' : trade.taker_side === 'SELL'
        return (
          <div
            key={trade.trade_id ?? `${trade.seq ?? i}`}
            className="ud-num flex h-[var(--ud-row-height-dense)] items-center px-2 text-body-sm"
          >
            <span className={cx('w-12', up ? 'text-up-text' : 'text-down-text')}>
              <Glyph name={up ? 'up' : 'down'} label={null} /> {trade.price_cents ?? '—'}
            </span>
            <span className="flex-1 text-right text-text-primary">
              {trade.size === undefined ? '—' : formatShares(trade.size)}
            </span>
            <span className="w-16 text-right text-text-tertiary">
              {formatClock(trade.executed_at_ms)}
            </span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * `mm:ss` of the print.
 *
 * Wall-clock here rather than seconds-past-anchor — unlike the chart's X axis —
 * because the tape outlives one market: during the roll it still shows the
 * previous market's last prints, and `:58` would be ambiguous between two
 * minutes while `21:46:58` is not.
 *
 * `new Date(ms)` is formatting, not a clock read: the value came off the wire
 * already stamped by the server. The D9 rule is about *reading* the browser's
 * clock, which this does not do.
 */
function formatClock(atMs: number | undefined): string {
  if (atMs === undefined) return '—'
  const date = new Date(atMs)
  return `${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}`
}
